/**
 * ResellerDashboard — referred clients, billing, and commission metrics for reseller role.
 *
 * Shows: Mis Clientes Referidos, Facturacion, commission breakdown (Pendiente/Aprobado/Pagado),
 * and contract status badge.
 * Quick link: Ver Mis Comisiones.
 */

import { Link } from 'react-router-dom'
import type { ResellerDashboardMetrics } from '@/hooks/useDashboard'
import { CommissionDashboard } from './CommissionDashboard'
import { formatCents } from './formatters'

// ── Contract status badge ─────────────────────────────────────────────────────

const CONTRACT_STATUS_COLORS: Record<string, string> = {
  draft:       'bg-gray-100 text-gray-600',
  sent:        'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  signed:      'bg-green-100 text-green-800',
  declined:    'bg-red-100 text-red-800',
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft:       'Borrador',
  sent:        'Enviado',
  in_progress: 'En proceso',
  signed:      'Firmado',
  declined:    'Rechazado',
}

function ContractStatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
        Sin contrato
      </span>
    )
  }
  const color = CONTRACT_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
  const label = CONTRACT_STATUS_LABELS[status] ?? status
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>
      {label}
    </span>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ResellerDashboardProps {
  metrics: ResellerDashboardMetrics
}

export function ResellerDashboard({ metrics }: ResellerDashboardProps) {
  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Dashboard de Reseller</h1>
          <p className="mt-1 text-sm text-gray-500">
            Tus clientes referidos y comisiones — actualizacion cada 5 minutos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Contrato:</span>
          <ContractStatusBadge status={metrics.contractStatus} />
        </div>
      </div>

      {/* Top metric cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Mis Clientes Referidos</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {metrics.myClients.toLocaleString('es-CO')}
          </p>
          <p className="mt-1 text-xs text-gray-400">clientes traidos por tus codigos de referido</p>
        </div>

        <div className="rounded-lg border border-green-200 bg-green-50 p-6 shadow-sm">
          <p className="text-sm font-medium text-green-700">Facturacion de Mis Clientes</p>
          <p className="mt-2 text-3xl font-bold text-green-900">
            {formatCents(metrics.myAccountsBillingCents)}
          </p>
          <p className="mt-1 text-xs text-green-600">facturacion mensual total de cuentas referidas</p>
        </div>
      </div>

      {/* Commission breakdown */}
      <CommissionDashboard
        pendingCommissionsCents={metrics.pendingCommissionsCents}
        approvedCommissionsCents={metrics.approvedCommissionsCents}
        paidCommissionsCents={metrics.paidCommissionsCents}
      />

      {/* Quick link */}
      <div className="mt-8">
        <Link
          to="/commissions"
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Ver Mis Comisiones
        </Link>
      </div>
    </div>
  )
}
